"use client";

import { useQuery } from "@tanstack/react-query";

import { env } from "@/env";

import type { Card, Stats, TicketConfig, Week } from "../data/types";

const BASE_URL = env.NEXT_PUBLIC_API_URL;

interface CampaignApiResponse {
  config: {
    current_week: number;
    week_1_start: string;
    ticket_size_usd: number;
    max_tickets: number;
    spot_multiplier: number;
    stats: Stats;
    total_tickets: number;
    participants: number;
    as_of: string | null;
  };
  weeks: Week[];
}

export interface CardvaultCampaign {
  currentWeek: number;
  ticketConfig: TicketConfig;
  campaignStart: Date;
  weeks: Week[];
  stats: Stats;
  cardsById: Map<number, Card>;
  totalTickets: number;
  participants: number;
  asOf: string | null;
}

const EMPTY_CAMPAIGN: CardvaultCampaign | null = null;

async function fetchCampaign(): Promise<CardvaultCampaign> {
  const res = await fetch(`${BASE_URL}/api/card-vault/campaign`);
  if (!res.ok) throw new Error(`Failed to load campaign: ${res.status}`);
  const json = (await res.json()) as CampaignApiResponse;

  const cardsById = new Map<number, Card>();
  for (const week of json.weeks) {
    for (const card of week.cards) cardsById.set(card.id, card);
  }

  return {
    currentWeek: json.config.current_week,
    ticketConfig: {
      ticketSizeUsd: json.config.ticket_size_usd,
      maxTickets: json.config.max_tickets,
      spotMultiplier: json.config.spot_multiplier,
    },
    campaignStart: new Date(json.config.week_1_start),
    weeks: json.weeks,
    stats: json.config.stats,
    cardsById,
    totalTickets: json.config.total_tickets,
    participants: json.config.participants,
    asOf: json.config.as_of,
  };
}

export function cardvaultCampaignQueryKey() {
  return ["cardvault-campaign"] as const;
}

export function useCardvaultCampaign() {
  const { data, isLoading, error } = useQuery({
    queryKey: cardvaultCampaignQueryKey(),
    queryFn: fetchCampaign,
    staleTime: 60_000,
    refetchInterval: 60_000,
  });
  return { campaign: data ?? EMPTY_CAMPAIGN, isLoading, error };
}
